import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getAuthors, createAuthor, updateAuthor, deleteAuthor } from '../../api/admin/client';

export default function AuthorsPage() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', title: '', bio: '', avatarUrl: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      const data = await getAuthors();
      setAuthors(data.authors || []);
    } catch (err) {
      console.error('Error fetching authors:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (author = null) => {
    if (author) {
      setEditingId(author.id);
      setForm({
        name: author.name,
        title: author.title || '',
        bio: author.bio || '',
        avatarUrl: author.avatarUrl || ''
      });
    } else {
      setEditingId(null);
      setForm({ name: '', title: '', bio: '', avatarUrl: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ name: '', title: '', bio: '', avatarUrl: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    try {
      setSaving(true);
      if (editingId) {
        await updateAuthor(editingId, form);
      } else {
        await createAuthor(form);
      }
      closeModal();
      fetchAuthors();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete author "${name}"?`)) return;

    try {
      await deleteAuthor(id);
      fetchAuthors();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <AdminLayout title="Authors">
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">All Authors</h3>
          <button onClick={() => openModal()} className="admin-btn admin-btn-primary">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Author
          </button>
        </div>
        <div className="admin-card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : authors.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty-icon">👤</div>
              <div className="admin-empty-title">No authors yet</div>
              <button onClick={() => openModal()} className="admin-btn admin-btn-primary" style={{ marginTop: '16px' }}>
                Add Author
              </button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Author</th>
                  <th>Title</th>
                  <th>Posts</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {authors.map(author => (
                  <tr key={author.id}>
                    <td>
                      <div className="admin-flex admin-items-center admin-gap-2">
                        <div
                          className="admin-user-avatar"
                          style={{
                            width: '36px', height: '36px',
                            backgroundImage: author.avatarUrl ? `url(${author.avatarUrl})` : 'none',
                            backgroundSize: 'cover'
                          }}
                        >
                          {!author.avatarUrl && author.name.substring(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 500 }}>{author.name}</span>
                      </div>
                    </td>
                    <td className="admin-text-muted">{author.title || '-'}</td>
                    <td>
                      <span className="admin-badge admin-badge-info">
                        {author.totalCount} posts
                      </span>
                    </td>
                    <td>
                      <div className="admin-flex admin-gap-2">
                        <button onClick={() => openModal(author)} className="admin-btn admin-btn-sm admin-btn-secondary">
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(author.id, author.name)}
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          disabled={author.totalCount > 0}
                          title={author.totalCount > 0 ? 'Cannot delete author with posts' : 'Delete'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">{editingId ? 'Edit Author' : 'New Author'}</h3>
              <button onClick={closeModal} className="admin-btn admin-btn-icon admin-btn-secondary">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label">Name *</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Author name"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Title</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Medical Director"
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Avatar URL</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.avatarUrl}
                    onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="admin-form-group admin-mb-0">
                  <label className="admin-label">Bio</label>
                  <textarea
                    className="admin-textarea"
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder="Author biography"
                    style={{ minHeight: '100px' }}
                  />
                </div>
              </div>
              <div className="admin-card-body" style={{ borderTop: '1px solid var(--admin-border)', paddingTop: '16px' }}>
                <div className="admin-flex admin-gap-2 admin-justify-between">
                  <button type="button" onClick={closeModal} className="admin-btn admin-btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
